// Tema claro/oscuro. La preferencia es visual y no personal: se guarda en este navegador (localStorage).
// «Según la hora» usa la zona del perfil: oscuro de 19:00 a 06:00.

export type Theme = 'light' | 'dark';
export type ThemePreference = 'light' | 'dark' | 'schedule' | 'system';

export const THEME_PREFERENCES: ThemePreference[] = ['light', 'dark', 'schedule', 'system'];
const PREFERENCE_KEY = 'hilo.tema';
const DISMISS_KEY = 'hilo.tema.sugerencia';
const NIGHT_FROM = 19;
const NIGHT_UNTIL = 6;
const CHANGE_EVENT = 'hilo:tema';

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function readPreference(): ThemePreference {
  const value = storage()?.getItem(PREFERENCE_KEY);
  return THEME_PREFERENCES.includes(value as ThemePreference) ? (value as ThemePreference) : 'system';
}

export function writePreference(preference: ThemePreference): void {
  storage()?.setItem(PREFERENCE_KEY, preference);
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

export function subscribePreference(onChange: () => void): () => void {
  const onStorage = (event: StorageEvent) => { if (event.key === PREFERENCE_KEY) onChange(); };
  window.addEventListener(CHANGE_EVENT, onChange);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(CHANGE_EVENT, onChange);
    window.removeEventListener('storage', onStorage);
  };
}

function localHour(now: Date, timeZone?: string): number {
  return Number(new Intl.DateTimeFormat('en-US', { hour: 'numeric', hourCycle: 'h23', ...(timeZone ? { timeZone } : {}) }).format(now));
}

/** De noche: de 19:00 a 05:59 en la zona indicada (la del navegador si no hay perfil). */
export function isNight(now: Date, timeZone?: string): boolean {
  const hour = localHour(now, timeZone);
  return hour >= NIGHT_FROM || hour < NIGHT_UNTIL;
}

export function resolveTheme(preference: ThemePreference, systemDark: boolean, now: Date, timeZone?: string): Theme {
  if (preference === 'light' || preference === 'dark') return preference;
  if (preference === 'schedule') return isNight(now, timeZone) ? 'dark' : 'light';
  return systemDark ? 'dark' : 'light';
}

export function applyTheme(theme: Theme): void {
  document.documentElement.dataset.theme = theme;
}

export function systemPrefersDark(): boolean {
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/** Clave de «esta noche»: la sugerencia descartada no vuelve a salir hasta la noche siguiente. */
export function nightKey(now: Date, timeZone?: string): string {
  // Antes de las 06:00 cuenta como la noche del día anterior.
  const shifted = new Date(now.getTime() - NIGHT_UNTIL * 3_600_000);
  return new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit', ...(timeZone ? { timeZone } : {}) }).format(shifted);
}

export function suggestionDismissed(key: string): boolean {
  return storage()?.getItem(DISMISS_KEY) === key;
}

export function dismissSuggestion(key: string): void {
  storage()?.setItem(DISMISS_KEY, key);
  window.dispatchEvent(new Event(CHANGE_EVENT));
}
